import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { ProtectedRoute } from '@/auth/ProtectedRoute';
import Layout from '@/components/Layout';
import { Loading } from '@/components/Loading';

// Eager — login + auth flow loads first paint, must not split.
import LoginPage from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import NotAdminPage from '@/pages/NotAdmin';

// Lazy — admin pages split into per-route chunks. Recharts (the heavy
// dep, ~350 KB gz) only ships when AI Usage opens; tables stay tiny.
const OverviewPage = lazy(() => import('@/pages/Overview'));
const UsersPage = lazy(() => import('@/pages/Users'));
const UserDetailPage = lazy(() => import('@/pages/UserDetail'));
const JobsPage = lazy(() => import('@/pages/Jobs'));
const BroadcastPage = lazy(() => import('@/pages/Broadcast'));
const BroadcastHistoryPage = lazy(() => import('@/pages/BroadcastHistory'));
const AiUsagePage = lazy(() => import('@/pages/AiUsage'));
const RuntimesPage = lazy(() => import('@/pages/Runtimes'));
const FeedbackPage = lazy(() => import('@/pages/Feedback'));
// v3 — Razorpay billing surfaces.
const PlansPage = lazy(() => import('@/pages/Plans'));
const SubscriptionsPage = lazy(() => import('@/pages/Subscriptions'));
const PaymentEventsPage = lazy(() => import('@/pages/PaymentEvents'));
// v3 — AI policy compliance review queue.
const AiFlagsPage = lazy(() => import('@/pages/AiFlags'));
// v3 — monthly tax exports (GST / TDS / FIRC).
const ExportsPage = lazy(() => import('@/pages/Exports'));

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/not-admin" element={<NotAdminPage />} />

      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            <Suspense fallback={<Loading />}>
              <OverviewPage />
            </Suspense>
          }
        />
        <Route
          path="users"
          element={
            <Suspense fallback={<Loading />}>
              <UsersPage />
            </Suspense>
          }
        />
        <Route
          path="users/:userId"
          element={
            <Suspense fallback={<Loading />}>
              <UserDetailPage />
            </Suspense>
          }
        />
        <Route
          path="jobs"
          element={
            <Suspense fallback={<Loading />}>
              <JobsPage />
            </Suspense>
          }
        />
        <Route
          path="broadcast"
          element={
            <Suspense fallback={<Loading />}>
              <BroadcastPage />
            </Suspense>
          }
        />
        <Route
          path="broadcast/history"
          element={
            <Suspense fallback={<Loading />}>
              <BroadcastHistoryPage />
            </Suspense>
          }
        />
        <Route
          path="ai-usage"
          element={
            <Suspense fallback={<Loading />}>
              <AiUsagePage />
            </Suspense>
          }
        />
        {/* v3 — Razorpay-driven billing pages */}
        <Route
          path="billing/plans"
          element={
            <Suspense fallback={<Loading />}>
              <PlansPage />
            </Suspense>
          }
        />
        <Route
          path="billing/subscriptions"
          element={
            <Suspense fallback={<Loading />}>
              <SubscriptionsPage />
            </Suspense>
          }
        />
        <Route
          path="billing/payment-events"
          element={
            <Suspense fallback={<Loading />}>
              <PaymentEventsPage />
            </Suspense>
          }
        />
        <Route
          path="ai-flags"
          element={
            <Suspense fallback={<Loading />}>
              <AiFlagsPage />
            </Suspense>
          }
        />
        <Route
          path="exports"
          element={
            <Suspense fallback={<Loading />}>
              <ExportsPage />
            </Suspense>
          }
        />
        <Route
          path="runtimes"
          element={
            <Suspense fallback={<Loading />}>
              <RuntimesPage />
            </Suspense>
          }
        />
        <Route
          path="feedback"
          element={
            <Suspense fallback={<Loading />}>
              <FeedbackPage />
            </Suspense>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
